<template lang="pug">
.c-stickers-page
  section.c-stickers-page__header
    .c-stickers-page__header-content
      h1.c-stickers-page__heading Sticker collections
      p.c-stickers-page__subtitle Hunt collections for {{ orgName || 'this organization' }}
    .c-stickers-page__header-actions
      el-button.c-stickers-action-btn(@click="$router.push(stickersPath)")
        fai(type="fal" icon="images")
        span Org stickers
      el-button.c-stickers-action-btn(type="primary" @click="goCreate")
        fai(type="fal" icon="plus-circle")
        span Create collection
  section.c-stickers-page__nav-section.d-flex.flex-wrap.align-items-center.gap-3
    .c-stickers-nav.c-stickers-nav--hub
      button.c-stickers-nav-link(type="button" @click="$router.push(stickersPath)") Stickers
      button.c-stickers-nav-link.c-stickers-nav-link--active(type="button") Collections
    .c-stickers-nav
      button.c-stickers-nav-link(
        v-for="tab in statusTabs"
        :key="tab.value"
        type="button"
        :class="{ 'c-stickers-nav-link--active': statusFilter === tab.value }"
        :disabled="loading"
        @click="setStatusFilter(tab.value)"
      ) {{ tab.label }}
  .c-stickers-content
    section.c-stickers-table-section
      el-table(
        v-loading="loading"
        :data="rows"
        style="width: 100%"
        @row-click="openRow"
      ).clickable-rows
        el-table-column(prop="title" label="Title" min-width="200")
        el-table-column(label="Slug" min-width="180")
          template(#default="{ row }")
            code.slug-cell {{ row.slug }}
        el-table-column(label="Status" width="130")
          template(#default="{ row }")
            span.status-badge(:class="statusClass(row.status)") {{ statusText(row.status) }}
        el-table-column(prop="totalStickers" label="Stickers" width="100")
        el-table-column(label="Published" width="160")
          template(#default="{ row }")
            span {{ formatPublished(row.publishedAt) }}
        el-table-column(label="" width="100" fixed="right")
          template(#default="{ row }")
            el-button(
              v-if="row.status === 'DRAFT'"
              size="small"
              type="danger"
              plain
              @click.stop="deleteRow(row)"
            ) Delete
            //- Published collections can't be deleted, only archived.
            el-button(
              v-else-if="row.status === 'PUBLISHED'"
              size="small"
              plain
              @click.stop="archiveRow(row)"
            ) Archive
      .d-flex.justify-content-between.align-items-center.p-3(v-if="rows.length || page > 1")
        el-button(:disabled="page <= 1 || loading" @click="prevPage") Previous
        span.text-muted Page {{ page }}
        el-button(:disabled="!hasMore || loading" @click="nextPage") Next
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import { ElMessage, ElMessageBox } from 'element-plus';
import globalShared from '@/shared';
import commonShared from '@/views/manage-users-and-organizations/shared';
import {
  findStickerCollections,
  mutateStickerCollectionStatus,
  removeStickerCollection,
} from '@/graphs/gql';
import {
  collectionStatusBadgeClass,
  collectionStatusLabel,
} from './shared';
import type { StickerCollectionListRow } from '@/data/stickerCollection.types';
import withGraphErrors from '@/utils/graphqlResponse';

const PAGE_SIZE = 20;

export default defineComponent({
  name: 'OrgStickerCollectionsList',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const { makeGraphRequest, getAuthToken, removeItemGetConfirm } = globalShared();

    const orgId = computed(() => String(route.params.orgId || ''));
    const { getOrganization } = commonShared();
    // The route's org can differ from the selected one (e.g. a super admin opening any org).
    const fetchedOrgName = ref('');
    const orgName = computed(() => {
      const selected = store.getters['organizations/GET_SELECTED_ORGANIZATION'];
      if (selected?.id === orgId.value && selected?.name) return selected.name;
      return fetchedOrgName.value;
    });

    async function loadOrgName() {
      const selected = store.getters['organizations/GET_SELECTED_ORGANIZATION'];
      if (!orgId.value || selected?.id === orgId.value) return;
      try {
        fetchedOrgName.value = (await getOrganization(orgId.value))?.name || '';
      } catch (e) {
        console.error(e);
      }
    }
    const stickersPath = computed(() => `/manage/orgs/${orgId.value}/stickers`);

    const loading = ref(false);
    const rows = ref<StickerCollectionListRow[]>([]);
    const statusFilter = ref('ALL');
    const page = ref(1);
    const hasMore = ref(false);

    const statusTabs = [
      { label: 'All', value: 'ALL' },
      { label: 'Draft', value: 'DRAFT' },
      { label: 'Published', value: 'PUBLISHED' },
      { label: 'Archived', value: 'ARCHIVED' },
    ];

    async function fetchList() {
      loading.value = true;
      try {
        const skip = (page.value - 1) * PAGE_SIZE;
        const response = await makeGraphRequest(
          'query',
          findStickerCollections,
          {
            orgId: orgId.value,
            status: statusFilter.value === 'ALL' ? null : statusFilter.value,
            skip,
            take: PAGE_SIZE,
          },
          getAuthToken(),
        );
        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to load collections');
          return;
        }
        rows.value = response?.data?.findStickerCollections ?? [];
        hasMore.value = rows.value.length >= PAGE_SIZE;
      } finally {
        loading.value = false;
      }
    }

    function setStatusFilter(value: string) {
      statusFilter.value = value;
      page.value = 1;
      fetchList();
    }

    function openRow(row: StickerCollectionListRow) {
      router.push(`/manage/orgs/${orgId.value}/sticker-collections/${row.id}`);
    }

    function goCreate() {
      router.push(`/manage/orgs/${orgId.value}/sticker-collections/new`);
    }

    function statusText(status: string) {
      return collectionStatusLabel(status);
    }

    function statusClass(status: string) {
      return collectionStatusBadgeClass(status);
    }

    function formatPublished(at?: string | null) {
      if (!at) return '—';
      return dayjs(at).format('MMM D, YYYY');
    }

    function deleteRow(row: StickerCollectionListRow) {
      removeItemGetConfirm(async () => {
        const response = await withGraphErrors(makeGraphRequest(
          'mutation',
          removeStickerCollection,
          { collectionId: row.id },
          getAuthToken(),
        ));
        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to delete collection');
          return false;
        }
        fetchList();
        return true;
      }, {
        title: 'Delete collection',
        message: `"${row.title}" will be permanently deleted. This can't be undone.`,
      });
    }

    async function archiveRow(row: StickerCollectionListRow) {
      try {
        await ElMessageBox.confirm(
          `Archive "${row.title}"? Archived collections cannot redeem codes.`,
          'Archive collection',
          { type: 'warning', customClass: 'c-confirm', confirmButtonText: 'Archive' },
        );
      } catch {
        return;
      }
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        mutateStickerCollectionStatus,
        { collectionId: row.id, status: 'ARCHIVED' },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || 'Failed to archive collection');
        return;
      }
      ElMessage.success('Collection archived');
      fetchList();
    }

    function prevPage() {
      if (page.value <= 1) return;
      page.value -= 1;
      fetchList();
    }

    function nextPage() {
      if (!hasMore.value) return;
      page.value += 1;
      fetchList();
    }

    onMounted(() => {
      loadOrgName();
      fetchList();
    });

    return {
      orgName,
      stickersPath,
      loading,
      rows,
      statusFilter,
      statusTabs,
      page,
      hasMore,
      setStatusFilter,
      openRow,
      goCreate,
      statusText,
      statusClass,
      formatPublished,
      deleteRow,
      archiveRow,
      prevPage,
      nextPage,
    };
  },
});
</script>

<style lang="scss" scoped>
$gray-600: #4b5563;
$gray-900: #111827;
$gray-100: #f3f4f6;

.c-stickers-page {
  padding: 2rem;
}

.c-stickers-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.c-stickers-page__heading {
  font-size: 1.875rem;
  font-weight: 600;
  margin: 0;
}

.c-stickers-page__subtitle {
  font-size: 0.875rem;
  color: $gray-600;
  margin: 0;
}

.c-stickers-page__header-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.c-stickers-action-btn {
  height: 36px;
  border-radius: 8px;
}

.c-stickers-nav {
  display: inline-flex;
  background-color: $gray-100;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 1rem;
}

.c-stickers-nav-link {
  border: none;
  background: transparent;
  padding: 6px 24px;
  border-radius: 10px;
  font-weight: 600;
  color: $gray-600;
  cursor: pointer;

  &--active {
    background: white;
    color: $gray-900;
  }
}

.c-stickers-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  overflow: hidden;
}

.clickable-rows :deep(.el-table__row) {
  cursor: pointer;
}

.slug-cell {
  font-size: 0.75rem;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;

  &--draft {
    background-color: #96c9ef;
  }

  &--published {
    background-color: #8dd8cd;
  }

  &--archived {
    background-color: #ebebeb;
    color: #666;
  }
}
</style>
